import type { PolygonLikeFC } from '@pn/services/map/mapbox/custom/PolygonPatternCustomLayer';
import earcut from 'earcut';
import { MercatorCoordinate } from 'mapbox-gl';

function ensureClosed<T extends readonly number[]>(
  ring: ReadonlyArray<T>
): T[] {
  if (ring.length < 3) return ring.slice();

  const [x0, y0] = ring[0];
  const [xN, yN] = ring[ring.length - 1];

  if (x0 === xN && y0 === yN) return ring.slice();
  return [...ring, ring[0]];
}

/**
 * Build one big triangle list for the entire FeatureCollection.
 * Output layout: [x,y,z, x,y,z, ...] where x/y are MercatorCoordinate units.
 */
export function buildVerticesFromFeatureCollection(
  fc: PolygonLikeFC
): Float32Array {
  const out: number[] = [];

  for (const feature of fc.features) {
    const geom = feature.geometry;
    if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon'))
      continue;

    const polygons =
      geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;

    for (const polygon of polygons) {
      if (!polygon || polygon.length === 0) continue;

      const flat: number[] = [];
      const holes: number[] = [];
      let vertCount = 0;

      for (let r = 0; r < polygon.length; r++) {
        const ring = ensureClosed(polygon[r]);
        if (ring.length < 4) continue;

        if (r > 0) holes.push(vertCount);

        for (const pos of ring) {
          const lng = pos[0];
          const lat = pos[1];
          const m = MercatorCoordinate.fromLngLat({ lng, lat }, 0);
          flat.push(m.x, m.y);
          vertCount++;
        }
      }

      if (vertCount < 3) continue;

      const indices = earcut(flat, holes, 2);

      for (let i = 0; i < indices.length; i++) {
        const vi = indices[i];
        out.push(flat[2 * vi + 0], flat[2 * vi + 1], 0);
      }
    }
  }

  return new Float32Array(out);
}
